// src/components/foods/FoodDealersPayment.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import "./foods.css";
import {
  fetchKhPartyList,
  fetchKhPartyPaymentList,
  saveKhPartyPayment,
} from "../../services/foods";

/* ---------------- utils ---------------- */
function pad2(n) {
  const x = String(n);
  return x.length === 1 ? `0${x}` : x;
}
function todayDmy() {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}
function fmt2(x) {
  const n = Number(x || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}
function safeParseArray(v) {
  try {
    const s = typeof v === "string" ? v.trim() : v;
    const out = typeof s === "string" ? JSON.parse(s) : s;
    return Array.isArray(out) ? out : [];
  } catch {
    return [];
  }
}
function isZero(n) {
  return Math.abs(Number(n || 0)) < 1e-9;
}
// Convert any locale digits to ASCII digits (Android logic)
function toAsciiDigits(input) {
  if (input == null) return "";
  const s = String(input);
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const ch = s.charAt(i);
    const digit = Number.isFinite(Number(ch)) ? Number(ch) : Number.NaN;
    // better: use numericValue-like mapping
    const nv = ch >= "0" && ch <= "9" ? ch.charCodeAt(0) - 48 : Number.NaN;
    if (!Number.isNaN(nv)) {
      out += ch;
      continue;
    }
    const numVal = (() => {
      const v = ch.codePointAt(0);
      // use JS trick: parseInt on unicode digits doesn't work; use numericValue via Intl? not available.
      // fallback: use Character.getNumericValue equivalent by mapping known ranges.
      // But easiest robust: use /\p{Nd}/u and charCode offsets:
      // Arabic-Indic, Devanagari, etc. We'll do generic by Number(ch) (often NaN) then keep.
      return Number.NaN;
    })();

    // If not ASCII digit, try a safer approach:
    // Use unicode digit property:
    if (/\p{Nd}/u.test(ch)) {
      // get digit by subtracting last digit codepoint in the set:
      // most scripts digits are contiguous 0..9, so use modulo 10
      const code = ch.codePointAt(0);
      out += String(code % 10);
    } else {
      out += ch;
    }
  }
  return out;
}

/* ---------------- Portal ---------------- */
function PortalModal({ children }) {
  return ReactDOM.createPortal(children, document.body);
}

/* ---------------- Dealer Picker Modal ---------------- */
function DealerPickerModal({ open, onClose, onPick, mainuser }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    if (!mainuser) return;

    (async () => {
      setLoading(true);
      try {
        const data = await fetchKhPartyList({ mainuser });
        const arr = safeParseArray(data);

        const mapped = arr.map((o, idx) => ({
          sr: o?.sr ?? String(idx + 1),
          kno: String(o?.kno ?? o?.sno ?? ""),
          name: String(o?.name ?? "").trim(),
          address: String(o?.address ?? "").trim(),
          mobno: String(o?.mobno ?? "").trim(),
          status: String(o?.status ?? "").trim(),
        }));

        setRows(mapped.filter((r) => r.kno));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, mainuser]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => {
      return (
        String(r.kno).toLowerCase().includes(s) ||
        String(r.name).toLowerCase().includes(s) ||
        String(r.address).toLowerCase().includes(s)
      );
    });
  }, [rows, q]);

  if (!open) return null;

  return (
    <PortalModal>
      <div className="fdBackdrop" onMouseDown={onClose}>
        <div className="fdModal fdPickModal" onMouseDown={(e) => e.stopPropagation()}>
          <div className="fdModalHead">
            <div className="fdPickHeadTitle">Food Dealers</div>
            <button type="button" className="fdCloseBtn" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="fdPickTop">
            <div className="fdPickCaption">Dealers</div>
            <div className="foodsSearchBox">
              <span className="foodsSearchLabel">Search</span>
              <input
                className="foodsInput"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
              />
            </div>
          </div>

          <div className="foodsTableWrap">
            <table className="foodsTable" style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={{ width: 120 }}>Code</th>
                  <th>Name</th>
                  <th>Address</th>
                  <th style={{ width: 160 }}>Mobile</th>
                  <th style={{ width: 120 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="foodsEmpty">
                      Loading...
                    </td>
                  </tr>
                ) : visible.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="foodsEmpty">
                      No dealers
                    </td>
                  </tr>
                ) : (
                  visible.map((r) => (
                    <tr
                      key={`${r.kno}-${r.sr}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => onPick(r)}
                      title="Click to select"
                    >
                      <td>{r.kno}</td>
                      <td>
                        <b>{r.name || "-"}</b>
                      </td>
                      <td>{r.address || "-"}</td>
                      <td>{r.mobno || "-"}</td>
                      <td>{r.status || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="foodsFooter">
            <div style={{ color: "#666" }}>
              Showing 1 to {visible.length} of {visible.length} entries
            </div>
            <div />
          </div>
        </div>
      </div>
    </PortalModal>
  );
}

/* ---------------- Add Dealer Payment Modal ---------------- */
function AddDealerPaymentModal({ open, onClose, onSaved, mainuser }) {
  const [kno, setKno] = useState("");
  const [cname, setCname] = useState("");
  const [address, setAddress] = useState("");

  const [dateDmy, setDateDmy] = useState(todayDmy());
  const [amt, setAmt] = useState("");
  const [remark, setRemark] = useState("");

  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Party index for manual code typing -> show name/address
  const [partyIndex, setPartyIndex] = useState(new Map());

  useEffect(() => {
    if (!open) return;

    // reset like screenshot default
    setKno("");
    setCname("");
    setAddress("");
    setDateDmy(todayDmy());
    setAmt("");
    setRemark("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!mainuser) return;

    (async () => {
      try {
        const data = await fetchKhPartyList({ mainuser });
        const arr = safeParseArray(data);

        const map = new Map();
        for (const o of arr) {
          const k = String(o?.kno ?? o?.sno ?? "").trim();
          if (!k) continue;
          const info = {
            name: String(o?.name ?? "").trim(),
            address: String(o?.address ?? "").trim(),
          };
          map.set(k, info);
          // normalize leading zeros
          const norm = k.replace(/^0+(?!$)/, "");
          if (norm && norm !== k) map.set(norm, info);
        }
        setPartyIndex(map);
      } catch {
        setPartyIndex(new Map());
      }
    })();
  }, [open, mainuser]);

  // update name/address from typed code
  useEffect(() => {
    const k = String(kno || "").trim().replace(/^0+(?!$)/, "");
    if (!k) {
      setCname("");
      setAddress("");
      return;
    }
    const p = partyIndex.get(k) || partyIndex.get(String(kno || "").trim());
    if (p) {
      setCname(p.name || "");
      setAddress(p.address || "");
    } else {
      setCname("");
      setAddress("");
    }
  }, [kno, partyIndex]);

  function validate() {
    if (!String(kno).trim()) return "Dealer code required";
    if (!String(dateDmy).trim()) return "Date required";
    if (!(Number(amt) > 0)) return "Amount Paid required";
    if (!String(remark).trim()) return "Comments required";
    return "";
  }

  async function onSave() {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        mainuser: String(mainuser || ""),
        date: toAsciiDigits(String(dateDmy || "")), // dd/MM/yyyy
        kno: String(kno || ""),
        type: "Credit", // ✅ always English like Android
        amt: toAsciiDigits(String(amt || "")),
        remark: String(remark || ""),
        sredit: "-", // new
        flag: "new",
      };

      const resText = await saveKhPartyPayment(payload);

      const ok =
        String(resText || "").toLowerCase().includes("saved") ||
        String(resText || "").toLowerCase().includes("updated");

      if (!ok) {
        // still refresh in case server returns different ok text
        // but show response for debugging
        // eslint-disable-next-line no-alert
        alert(String(resText || "Save failed"));
        return;
      }

      await onSaved?.();
      onClose();
    } catch (e) {
      alert(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <>
      <PortalModal>
        <div className="fdBackdrop" onMouseDown={onClose}>
          <div className="fdModal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="fdModalHead">
              <div className="fdModalHeadTitle">Add Dealer Payment Transaction</div>
              <button type="button" className="fdCloseBtn" onClick={onClose} aria-label="Close">
                ×
              </button>
            </div>

            <div className="fdModalBody">
              <div className="fdFormGrid">
                <div className="fdLabel">
                  Dealer : <span className="fdReq">*</span>
                </div>

                <div className="fdFieldRow">
                  <input
                    className="fdInput"
                    value={kno}
                    onChange={(e) => setKno(e.target.value)}
                    placeholder="Code"
                  />
                  <button
                    type="button"
                    className="fdIconBtn"
                    title="Select dealer"
                    onClick={() => setPickerOpen(true)}
                  >
                    🔍
                  </button>
                </div>

                <div />
                <div style={{ fontSize: 12, opacity: 0.85 }}>
                  {cname ? <b>{cname}</b> : "\u00A0"}
                  {address ? ` • ${address}` : ""}
                </div>

                <div className="fdLabel">
                  Date : <span className="fdReq">*</span>
                </div>
                <div>
                  <input
                    className="fdInput"
                    value={dateDmy}
                    onChange={(e) => setDateDmy(e.target.value)}
                    placeholder="dd/MM/yyyy"
                  />
                </div>

                <div className="fdLabel">
                  Amount Paid : <span className="fdReq">*</span>
                </div>
                <div>
                  <input
                    className="fdInput"
                    value={amt}
                    onChange={(e) => setAmt(e.target.value)}
                    placeholder=""
                  />
                </div>

                <div className="fdLabel">
                  Comments : <span className="fdReq">*</span>
                </div>
                <div>
                  <textarea
                    className="fdInput"
                    style={{ minHeight: 70, resize: "vertical" }}
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                  />
                </div>
              </div>

              <div className="fdActions">
                <button
                  type="button"
                  className="fdBtnGhost"
                  disabled={saving}
                  onClick={() => {
                    setKno("");
                    setCname("");
                    setAddress("");
                    setDateDmy(todayDmy());
                    setAmt("");
                    setRemark("");
                  }}
                >
                  Reset
                </button>

                <button type="button" className="fdBtnPrimary" disabled={saving} onClick={onSave}>
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </PortalModal>

      <DealerPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        mainuser={mainuser}
        onPick={(r) => {
          setKno(String(r.kno || ""));
          setCname(String(r.name || ""));
          setAddress(String(r.address || ""));
          setPickerOpen(false);
        }}
      />
    </>
  );
}

/* ---------------- Main Screen ---------------- */
export default function FoodDealersPayment({ mainuser, onBack }) {
  const [loading, setLoading] = useState(false);
  const [master, setMaster] = useState([]);
  const [includePaid, setIncludePaid] = useState(false);
  const [search, setSearch] = useState("");

  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    if (!mainuser) return;

    setLoading(true);
    try {
      const data = await fetchKhPartyPaymentList({ mainuser });
      const arr = safeParseArray(data);

      const rows = arr.map((o, idx) => {
        const bal = Number(o?.current_balance ?? 0) || 0;
        const credit = bal > 0 ? bal : 0;
        const debit = bal < 0 ? Math.abs(bal) : 0;

        return {
          srno: idx + 1,
          kno: String(o?.kno ?? ""),
          name: String(o?.name ?? ""),
          credit,
          debit,
          total: bal,
        };
      });

      setMaster(rows);
    } finally {
      setLoading(false);
    }
  }, [mainuser]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return master.filter((r) => {
      if (!includePaid && isZero(r.total)) return false;
      if (!q) return true;
      return (
        String(r.name || "").toLowerCase().includes(q) ||
        String(r.kno || "").toLowerCase().includes(q)
      );
    });
  }, [master, includePaid, search]);

  const totals = useMemo(() => {
    let credit = 0,
      debit = 0,
      total = 0;
    for (const r of visible) {
      credit += Number(r.credit || 0) || 0;
      debit += Number(r.debit || 0) || 0;
      total += Number(r.total || 0) || 0;
    }
    return { credit, debit, total };
  }, [visible]);

  return (
    <div className="foodsPage">
      <div className="foodsTop">
        <div className="foodsTitle" style={{ gap: 10 }}>
          <button className="foodsLinkBtn" type="button" onClick={onBack} title="Back">
            ←
          </button>
          <span>Food Dealers Payment</span>
        </div>

        <div className="foodsActions">
          <button className="foodsLinkBtn" type="button" onClick={() => setAddOpen(true)}>
            ＋ Add Dealer Payment Transaction
          </button>
        </div>
      </div>

      <div style={{ padding: "6px 0 14px 2px" }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={includePaid}
            onChange={(e) => setIncludePaid(e.target.checked)}
          />
          Include Paid
        </label>
      </div>

      <div className="foodsCard">
        <div className="foodsCardHead">
          <div className="foodsCaption">Food Dealers Payment</div>

          <div className="foodsSearchBox">
            <span className="foodsSearchLabel">Search</span>
            <input
              className="foodsInput"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
            />
          </div>
        </div>

        <div className="foodsTableWrap">
          <table className="foodsTable">
            <thead>
              <tr>
                <th>SrNo</th>
                <th>Vendor Name</th>
                <th className="foodsNum">Credit</th>
                <th className="foodsNum">Debit</th>
                <th className="foodsNum">Total Amount</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="foodsEmpty">
                    Loading...
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={5} className="foodsEmpty">
                    No rows
                  </td>
                </tr>
              ) : (
                <>
                  {visible.map((r) => (
                    <tr key={`${r.kno}-${r.srno}`}>
                      <td>{r.srno}</td>
                      <td>
                        <b>{r.name || "-"}</b>
                      </td>
                      <td className="foodsNum">{fmt2(r.credit)}</td>
                      <td className="foodsNum">{fmt2(r.debit)}</td>
                      <td className="foodsNum" style={{ color: r.total < 0 ? "red" : undefined }}>
                        {fmt2(r.total)}
                      </td>
                    </tr>
                  ))}

                  <tr className="foodsTotalRow">
                    <td colSpan={2}>
                      <b>Total Amount</b>
                    </td>
                    <td className="foodsNum">{fmt2(totals.credit)}</td>
                    <td className="foodsNum">{fmt2(totals.debit)}</td>
                    <td className="foodsNum" style={{ color: totals.total < 0 ? "red" : undefined }}>
                      {fmt2(totals.total)}
                    </td>
                  </tr>

                  <tr>
                    <td colSpan={5} style={{ textAlign: "center", padding: 12, color: "#666" }}>
                      Showing {visible.length ? 1 : 0} to {visible.length} of {visible.length} entries
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AddDealerPaymentModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={load}
        mainuser={mainuser}
      />
    </div>
  );
}
